import {
  readExplicitLanguageChoice,
  resolveInitialLanguage,
} from "@/common/languagePreference";
import { PlugConfig } from "@/types/plugConfig";
import plugConfigApi from "@/types/plugConfig/plugConfigApi";
import { applyDayjsLanguage } from "@/Utils/dayjs";
import { configureNumberingSystem } from "@/Utils/locale-format";
import { mergePlugConfigs } from "@/Utils/plugConfig";
import { callApi } from "@/Utils/request/query";
import mansoqConfig, { tenantDefaultLanguage } from "@mansoqConfig";
import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import HttpApi from "i18next-http-backend";
import resourcesToBackend from "i18next-resources-to-backend";
import { initReactI18next } from "react-i18next";
import { z } from "zod";

export const LANGUAGES = {
  en: "English",
  ta: "தமிழ்",
  ml: "മലയാളം",
  mr: "मराठी",
  kn: "ಕನ್ನಡ",
  hi: "हिन्दी",
  ar: "العربية",
} as const;

configureNumberingSystem(mansoqConfig.numberingSystem);

/**
 * Keep `<html dir>` and `<html lang>` in step with the active language so
 * logical Tailwind utilities (ms-/me-, ps-/pe-, start-/end-, text-start/end)
 * flip for right-to-left scripts such as Arabic, and move dayjs month and
 * weekday names to the same language.
 */
export function applyDocumentLanguage(language: string | undefined) {
  if (!language) {
    return;
  }
  applyDayjsLanguage(language);
  if (typeof document === "undefined") {
    return;
  }
  document.documentElement.lang = language;
  document.documentElement.dir = i18n.dir(language);
}

const LANGUAGE_PREFERENCE_DETECTOR = "mansoqLanguagePreference";

const browserStorage = () => {
  try {
    return typeof window === "undefined" ? undefined : window.localStorage;
  } catch {
    return undefined;
  }
};

const languageDetector = new LanguageDetector();
languageDetector.addDetector({
  name: LANGUAGE_PREFERENCE_DETECTOR,
  lookup: () =>
    resolveInitialLanguage({
      explicitChoice: readExplicitLanguageChoice(browserStorage()),
      tenantDefault: tenantDefaultLanguage(),
      navigatorLanguages:
        typeof navigator === "undefined"
          ? []
          : navigator.languages?.length
            ? navigator.languages
            : [navigator.language],
      availableLocales: Object.keys(LANGUAGES).filter((language) =>
        mansoqConfig.availableLocales.includes(language),
      ),
    }),
});

const DEFAULT_NAMESPACE = "mansoq-web";

let pluginConfigs: PlugConfig[] = [];

const namespaceToUrl = (namespace: string) => {
  if (namespace === DEFAULT_NAMESPACE) {
    return "/";
  }

  const pluginConfig = pluginConfigs.find(
    (config) => config.meta?.name === namespace || config.slug === namespace,
  );

  if (typeof pluginConfig?.meta?.localPath === "string") {
    return pluginConfig.meta.localPath;
  }

  if (
    pluginConfig?.meta?.url &&
    z.url().safeParse(pluginConfig.meta.url).success
  ) {
    const url = new URL(pluginConfig.meta.url);
    return url.origin.toString();
  }

  return undefined;
};

const fetchOptions = { cache: "no-store" as RequestCache };

export async function initI18n() {
  // Fetch plugin configurations from API
  try {
    const response = await callApi(plugConfigApi.list, {
      silent: true,
    });
    pluginConfigs = mergePlugConfigs(response.configs || []);
  } catch (error) {
    console.warn(
      "Failed to fetch plugin configurations for i18n namespaces:",
      error,
    );
    pluginConfigs = mergePlugConfigs();
  }

  const pluginNamespaces = pluginConfigs
    .map((config) => config.meta?.name || config.slug)
    .filter((name): name is string => !!name);

  const namespaces = Array.from(
    new Set([DEFAULT_NAMESPACE, ...pluginNamespaces]),
  );

  i18n.on("languageChanged", applyDocumentLanguage);

  void i18n
    .use(HttpApi)
    .use(initReactI18next)
    .use(languageDetector)
    .use(
      resourcesToBackend((language, namespace, callback) => {
        if (namespace === DEFAULT_NAMESPACE && mansoqConfig.i18nUrl) {
          const remoteUrl = `${mansoqConfig.i18nUrl}/${language}.json`;
          const localUrl = `/locale/${language}.json`;
          Promise.all([
            fetch(remoteUrl, fetchOptions)
              .then((response) => {
                if (!response.ok) {
                  throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response.json();
              })
              .catch((error) => {
                console.warn(
                  `Failed to load remote translations: ${remoteUrl}`,
                  error,
                );
                return {};
              }),
            fetch(localUrl, fetchOptions)
              .then((response) => {
                if (!response.ok) {
                  throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response.json();
              })
              .catch((error) => {
                console.warn(
                  `Failed to load local fallback translations: ${localUrl}`,
                  error,
                );
                return {};
              }),
          ])
            .then(([remoteResources, localResources]) => {
              const merged = { ...localResources, ...remoteResources };
              callback(null, merged);
            })
            .catch((error) => {
              console.error(
                `Failed to prepare translations for ${language}/${namespace}:`,
                error,
              );
              callback(error, null);
            });
          return;
        }

        const baseUrl = namespaceToUrl(namespace)?.replace(/\/$/, "");

        if (!baseUrl && namespace !== DEFAULT_NAMESPACE) {
          callback(null, {});
          return;
        }

        fetch(`${baseUrl}/locale/${language}.json`, fetchOptions)
          .then((response) => {
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            return response.json();
          })
          .then((resources) => {
            callback(null, resources);
          })
          .catch((error) => {
            console.error(
              `Failed to load translations for ${language}/${namespace}:`,
              error,
            );
            callback(error, null);
          });
      }),
    )
    .init({
      fallbackLng: "en",
      ns: namespaces,
      fallbackNS: pluginNamespaces,
      load: "currentOnly",
      supportedLngs: Object.keys(LANGUAGES),
      detection: {
        order: [LANGUAGE_PREFERENCE_DETECTOR],
        caches: [],
      },
      interpolation: {
        escapeValue: false,
        skipOnVariables: false,
      },
      defaultNS: DEFAULT_NAMESPACE,
    })
    .then(() => applyDocumentLanguage(i18n.resolvedLanguage ?? i18n.language));
}

/**
 * @public
 */
export default i18n;
